import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { analisisIAAPI } from '../services/api';

const MODULOS = [
  { key: 'asistente_propietario', nombre: 'Asistente IA del Propietario', icono: 'bi-chat-dots-fill', color: '#6366f1', desc: 'Chat en lenguaje natural con datos reales del negocio' },
  { key: 'analisis_financiero', nombre: 'Análisis Financiero', icono: 'bi-graph-up-arrow', color: '#10b981', desc: 'P&L, flujo de caja, márgenes y tendencias' },
  { key: 'prediccion_inventario', nombre: 'Predicción de Inventario', icono: 'bi-box-seam-fill', color: '#f59e0b', desc: 'Reorder points, slow movers, recomendación de compra' },
  { key: 'deteccion_fraude', nombre: 'Detección de Fraude', icono: 'bi-shield-exclamation', color: '#ef4444', desc: 'Anulaciones sospechosas, diferencias caja, patrones anómalos' },
  { key: 'resumen_diario', nombre: 'Resumen Diario', icono: 'bi-calendar-check-fill', color: '#8b5cf6', desc: 'Resumen automático del día con KPIs y alertas' },
  { key: 'recomendacion_precios', nombre: 'Recomendación de Precios', icono: 'bi-tags-fill', color: '#ec4899', desc: 'Análisis de márgenes y sugerencias de ajuste de precios' },
  { key: 'analisis_clientes', nombre: 'Análisis de Clientes', icono: 'bi-people-fill', color: '#06b6d4', desc: 'Segmentación, predicción retorno, alertas de cobro' },
  { key: 'dashboard_ejecutivo', nombre: 'Dashboard Ejecutivo', icono: 'bi-speedometer2', color: '#0ea5e9', desc: 'Métricas explicadas con IA para toma de decisiones' }
];

const FRECUENCIAS = [
  { value: 'min_15', label: 'Cada 15 min', min: 15, hrs: 0, dias: 0 },
  { value: 'min_30', label: 'Cada 30 min', min: 30, hrs: 0, dias: 0 },
  { value: 'hrs_1', label: 'Cada 1 hora', min: 0, hrs: 1, dias: 0 },
  { value: 'hrs_2', label: 'Cada 2 horas', min: 0, hrs: 2, dias: 0 },
  { value: 'hrs_6', label: 'Cada 6 horas', min: 0, hrs: 6, dias: 0 },
  { value: 'hrs_12', label: 'Cada 12 horas', min: 0, hrs: 12, dias: 0 },
  { value: 'hrs_24', label: 'Diario (24h)', min: 0, hrs: 24, dias: 0 },
  { value: 'dias_2', label: 'Cada 2 días', min: 0, hrs: 0, dias: 2 },
  { value: 'dias_3', label: 'Cada 3 días', min: 0, hrs: 0, dias: 3 },
  { value: 'dias_7', label: 'Semanal', min: 0, hrs: 0, dias: 7 },
  { value: 'dias_15', label: 'Quincenal', min: 0, hrs: 0, dias: 15 },
  { value: 'dias_30', label: 'Mensual', min: 0, hrs: 0, dias: 30 },
];

const SPINNER_PHASES = ['Consultando base de datos...', 'Analizando con IA...', 'Procesando resultados...', 'Generando reporte...'];

const SYSTEM_ROUTES = {
  '/productos': 'Productos', '/inventario': 'Inventario', '/compras': 'Compras',
  '/ventas': 'Punto de Venta', '/caja': 'Caja', '/gastos': 'Gastos',
  '/clientes': 'Clientes', '/ordenes-trabajo': 'Órdenes de Trabajo',
  '/cuentas-por-cobrar': 'Cuentas por Cobrar', '/reportes': 'Reportes',
  '/proveedores': 'Proveedores', '/facturacion': 'Facturación',
  '/conciliacion-bancaria': 'Conciliación', '/configuracion': 'Configuración'
};

// ─── HTML Sanitizer (XSS protection) ───
const sanitizeHTML = (html) => {
  if (!html) return '';
  return html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/on\w+="[^"]*"/gi, '')
    .replace(/on\w+='[^']*'/gi, '')
    .replace(/javascript:/gi, '');
};

// ─── Markdown to HTML converter ───
const renderMarkdown = (text) => {
  if (!text) return '';
  let html = text
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    // Headers
    .replace(/^### (.+)$/gm, '<h6 class="fw-bold text-primary mt-3 mb-1">$1</h6>')
    .replace(/^## (.+)$/gm, '<h5 class="fw-bold text-primary mt-3 mb-2">$1</h5>')
    // Bold & italic
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // Code
    .replace(/`([^`]+)`/g, '<code class="bg-light px-1 rounded">$1</code>')
    // Tables — detect header vs separator vs data rows
    .replace(/^\|(.+)\|$/gm, (match) => {
      const cells = match.split('|').filter(c => c.trim());
      // Separator row (|---|---|) → mark for header detection
      if (cells.every(c => /^[\s\-:]+$/.test(c))) return '<!--table-sep-->';
      return '<tr>' + cells.map(c => `<td class="px-2 py-1 border" style="font-size:0.8rem">${c.trim()}</td>`).join('') + '</tr>';
    })
    // Lists
    .replace(/^- (.+)$/gm, '<li class="mb-1">$1</li>')
    .replace(/^(\d+)\. (.+)$/gm, '<li class="mb-1">$2</li>')
    // Horizontal rule
    .replace(/^---$/gm, '<hr class="my-2">')
    // Blockquotes
    .replace(/^&gt; (.+)$/gm, '<div class="border-start border-3 border-primary ps-3 py-1 bg-light rounded-end my-2" style="font-size:0.85rem">$1</div>')
    // Line breaks
    .replace(/\n\n/g, '</p><p class="mb-2">')
    .replace(/\n/g, '<br>');
  // Wrap li in ul
  html = html.replace(/(<li[^>]*>.*?<\/li>\s*)+/g, '<ul class="list-unstyled ms-2 mb-2">$&</ul>');
  // Wrap table rows — promote first row before separator to <th>
  html = html.replace(/((<tr>.*?<\/tr>\s*)(<!--table-sep-->\s*)?)+/g, (block) => {
    const parts = block.split('<!--table-sep-->');
    if (parts.length >= 2 && parts[0].trim()) {
      // First part is header row, rest are data rows
      const headerRow = parts[0].replace(/<td /g, '<th ').replace(/<\/td>/g, '</th>');
      const bodyRows = parts.slice(1).join('');
      return `<table class="table table-sm table-bordered mb-2" style="font-size:0.8rem"><thead class="table-primary">${headerRow}</thead><tbody>${bodyRows}</tbody></table>`;
    }
    return `<table class="table table-sm table-bordered mb-2" style="font-size:0.8rem">${block.replace(/<!--table-sep-->/g, '')}</table>`;
  });
  // System route links
  Object.entries(SYSTEM_ROUTES).forEach(([route, name]) => {
    const regex = new RegExp(`(${route.replace('/', '\\/')})(?=[\\s,.)\\]"']|$)`, 'g');
    html = html.replace(regex, `<a href="${route}" class="text-primary fw-bold text-decoration-none"><i class="bi bi-box-arrow-up-right" style="font-size:0.7rem"></i> ${name}</a>`);
  });
  return `<p class="mb-2">${html}</p>`;
};

const AnalisisIA = () => {
  const navigate = useNavigate();
  const { usuario } = useAuth();
  const [activeTab, setActiveTab] = useState('modulos');
  const [config, setConfig] = useState({});
  const [schedule, setSchedule] = useState([]);
  const [resultados, setResultados] = useState([]);
  const [alertas, setAlertas] = useState([]);
  const [selectedResult, setSelectedResult] = useState(null);
  const [running, setRunning] = useState({});
  const [runPhase, setRunPhase] = useState({});
  const [loading, setLoading] = useState(true);
  const [iaServiceOnline, setIaServiceOnline] = useState(null);
  const [filtroModulo, setFiltroModulo] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');

  // Chat state
  const [chatMessages, setChatMessages] = useState([
    { role: 'assistant', content: 'Hola, soy tu asistente IA con acceso a los datos reales de tu negocio. Puedes preguntarme sobre ventas, inventario, gastos, clientes, o cualquier aspecto de tu óptica. ¿En qué puedo ayudarte?' }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatSessionId, setChatSessionId] = useState(null);
  const chatEndRef = useRef(null);
  const [configForm, setConfigForm] = useState({ apiKey: '', modelo: 'claude-sonnet-4-6', emailDestinatario: '', smtpHost: '', smtpPort: '587', smtpUser: '', smtpPassword: '', smtpFrom: '' });
  const [smtpConfigured, setSmtpConfigured] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const results = await Promise.allSettled([
        analisisIAAPI.getConfig(), analisisIAAPI.getSchedule(),
        analisisIAAPI.getResultados({ limit: 50 }), analisisIAAPI.healthCheck(),
        analisisIAAPI.getAlertas()
      ]);
      const [configRes, scheduleRes, resultadosRes, healthRes, alertasRes] = results.map(r => r.status === 'fulfilled' ? r.value : null);
      if (configRes) setConfig(configRes.data?.data || {});
      if (scheduleRes) setSchedule(scheduleRes.data?.data || []);
      if (resultadosRes) setResultados(resultadosRes.data?.data || []);
      if (alertasRes) setAlertas(alertasRes.data?.data || []);
      setIaServiceOnline(healthRes?.data?.success && healthRes?.data?.ia_service?.status === 'ok');
      const modelo = configRes?.data?.data?.modelo_claude;
      if (modelo?.valor && modelo.valor !== '••••••••') setConfigForm(f => ({ ...f, modelo: modelo.valor }));
      const email = configRes?.data?.data?.email_destinatario;
      if (email?.valor) setConfigForm(f => ({ ...f, emailDestinatario: email.valor }));
      // SMTP config
      const smtp = configRes?.data?.data;
      if (smtp?.smtp_host?.valor) {
        setConfigForm(f => ({ ...f, smtpHost: smtp.smtp_host.valor, smtpPort: smtp.smtp_port?.valor || '587', smtpUser: smtp.smtp_user?.valor || '', smtpFrom: smtp.smtp_from?.valor || '' }));
        setSmtpConfigured(true);
      }
    } catch (err) { console.error('Error loading IA data:', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

  // ─── Spinner phase animation ───
  useEffect(() => {
    const activeModules = Object.keys(running).filter(k => running[k]);
    if (activeModules.length === 0) return;
    const interval = setInterval(() => {
      setRunPhase(p => {
        const next = { ...p };
        activeModules.forEach(m => { next[m] = ((p[m] || 0) + 1) % SPINNER_PHASES.length; });
        return next;
      });
    }, 4000);
    return () => clearInterval(interval);
  }, [running]);

  // ─── Analysis execution (synchronous — no polling, with 5-min timeout) ───
  const ejecutarModulo = async (modulo) => {
    if (!window.confirm('¿Ejecutar análisis? Esto consumirá tokens de la API de Claude.')) return;
    setRunning(r => ({ ...r, [modulo]: true }));
    setRunPhase(p => ({ ...p, [modulo]: 0 }));
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5 * 60 * 1000); // 5 minutes
    try {
      const res = await analisisIAAPI.ejecutar(modulo, undefined, { signal: controller.signal });
      if (res.data?.success) {
        toast.success(`"${MODULOS.find(m => m.key === modulo)?.nombre}" completado`);
        setSelectedResult(res.data.data);
        loadData();
      } else {
        toast.error(res.data?.error || 'Error al ejecutar análisis');
      }
    } catch (err) {
      if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
        toast.error('El análisis tardó demasiado (más de 5 minutos). Intenta de nuevo más tarde.');
      } else {
        toast.error(err.response?.data?.error || 'Error al conectar con el servicio de IA');
      }
    } finally {
      clearTimeout(timeout);
      setRunning(r => ({ ...r, [modulo]: false }));
    }
  };

  const saveConfig = async () => {
    try {
      if (configForm.apiKey) await analisisIAAPI.updateConfig('anthropic_api_key', configForm.apiKey);
      await analisisIAAPI.updateConfig('modelo_claude', configForm.modelo);
      await analisisIAAPI.updateConfig('email_destinatario', configForm.emailDestinatario);
      // SMTP config
      if (configForm.smtpHost) {
        await analisisIAAPI.updateConfig('smtp_host', configForm.smtpHost);
        await analisisIAAPI.updateConfig('smtp_port', configForm.smtpPort || '587');
        await analisisIAAPI.updateConfig('smtp_user', configForm.smtpUser);
        if (configForm.smtpPassword) await analisisIAAPI.updateConfig('smtp_password', configForm.smtpPassword);
        await analisisIAAPI.updateConfig('smtp_from', configForm.smtpFrom || configForm.smtpUser);
      }
      toast.success('Configuración guardada');
      setConfigForm(f => ({ ...f, apiKey: '', smtpPassword: '' }));
      loadData();
    } catch (err) { toast.error('Error al guardar configuración'); }
  };

  const toggleSchedule = async (modulo, habilitado) => {
    try { await analisisIAAPI.updateSchedule(modulo, { habilitado }); loadData(); }
    catch (err) { toast.error('Error al actualizar programación'); }
  };

  const updateFrequency = async (modulo, freqKey) => {
    const freq = FRECUENCIAS.find(f => f.value === freqKey);
    if (!freq) return;
    try { await analisisIAAPI.updateSchedule(modulo, { frecuencia_minutos: freq.min, frecuencia_horas: freq.hrs, frecuencia_dias: freq.dias }); loadData(); }
    catch (err) { toast.error('Error al actualizar frecuencia'); }
  };

  const getFreqValue = (s) => {
    if (s.frecuencia_dias > 0) return FRECUENCIAS.find(f => f.dias === s.frecuencia_dias)?.value || 'hrs_24';
    if (s.frecuencia_minutos > 0) return FRECUENCIAS.find(f => f.min === s.frecuencia_minutos)?.value || 'hrs_24';
    return FRECUENCIAS.find(f => f.hrs === (s.frecuencia_horas || 24))?.value || 'hrs_24';
  };
  const getFreqLabel = (s) => FRECUENCIAS.find(f => f.value === getFreqValue(s))?.label || `${s.frecuencia_horas || 24}h`;

  // ─── Chat (with 5-min timeout) ───
  const sendChat = async (directMsg) => {
    const msg = (directMsg || chatInput).trim();
    if (!msg || chatLoading) return;
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: msg }]);
    setChatLoading(true);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5 * 60 * 1000); // 5 minutes
    try {
      const res = await analisisIAAPI.chat(msg, chatSessionId, { signal: controller.signal });
      if (res.data?.success) {
        const data = res.data.data;
        setChatSessionId(data.session_id);
        setChatMessages(prev => [...prev, { role: 'assistant', content: data.respuesta, tokens: data.tokens_usados, time: data.tiempo_ms }]);
      } else {
        setChatMessages(prev => [...prev, { role: 'assistant', content: 'Error: ' + (res.data?.error || 'No se pudo procesar') }]);
      }
    } catch (err) {
      if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
        setChatMessages(prev => [...prev, { role: 'assistant', content: 'La respuesta tardó demasiado (más de 5 minutos). Intenta reformular tu pregunta o intenta de nuevo más tarde.' }]);
      } else {
        setChatMessages(prev => [...prev, { role: 'assistant', content: 'Error al conectar con el servicio de IA. Verifica que el API Key esté configurada.' }]);
      }
    } finally { clearTimeout(timeout); setChatLoading(false); }
  };

  const exportar = async (id, fmt) => {
    try {
      const res = fmt === 'pdf' ? await analisisIAAPI.exportarPDF(id) : await analisisIAAPI.exportarExcel(id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `analisis_${id}.${fmt === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success(`Exportado como ${fmt.toUpperCase()}`);
    } catch { toast.error('Error al exportar'); }
  };

  const enviarEmailResult = async (id) => {
    try {
      const res = await analisisIAAPI.enviarEmail(id);
      if (res.data?.success) toast.success(res.data.message);
      else toast.error(res.data?.error || 'Error al enviar email');
    } catch (err) { toast.error(err.response?.data?.error || 'Error al enviar email'); }
  };

  const filteredResultados = resultados.filter(r => {
    if (filtroModulo && r.modulo !== filtroModulo) return false;
    if (filtroTipo && r.tipo !== filtroTipo) return false;
    return true;
  });

  const getScheduleForModule = (m) => schedule.find(s => s.modulo === m) || {};
  const getLastResult = (m) => resultados.find(r => r.modulo === m);
  const formatDate = (d) => d ? new Date(d).toLocaleString('es-EC', { dateStyle: 'short', timeStyle: 'short' }) : 'Nunca';

  // ─── Professional Result Renderer ───
  const renderValue = (key, value) => {
    if (value === null || value === undefined) return null;
    const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

    // Risk level badge
    if (key.includes('riesgo') || key.includes('nivel') || key === 'estado_general' || key === 'estado_dia') {
      const colors = { critico: 'danger', alto: 'warning', medio: 'info', bajo: 'success', bueno: 'success', excelente: 'success', regular: 'warning' };
      const c = colors[String(value).toLowerCase()] || 'secondary';
      return <div key={key} className="mb-2"><span className="text-muted small">{label}: </span><span className={`badge bg-${c} ${c === 'warning' ? 'text-dark' : ''}`}>{String(value).toUpperCase()}</span></div>;
    }
    // Score
    if (key.includes('score') && typeof value === 'number') {
      const c = value >= 70 ? 'success' : value >= 40 ? 'warning' : 'danger';
      return <div key={key} className="mb-2"><span className="text-muted small">{label}: </span><span className={`badge bg-${c} fs-6`}>{value}/100</span></div>;
    }
    // String
    if (typeof value === 'string') {
      return <div key={key} className="mb-3"><h6 className="text-primary fw-bold mb-1" style={{ fontSize: '0.8rem' }}>{label}</h6><div className="small" style={{ lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: sanitizeHTML(renderMarkdown(value)) }} /></div>;
    }
    // Number
    if (typeof value === 'number') {
      return <div key={key} className="d-inline-block me-3 mb-2 text-center p-2 bg-light rounded" style={{ minWidth: 80 }}><div className="fw-bold text-primary">{value.toLocaleString()}</div><small className="text-muted" style={{ fontSize: '0.65rem' }}>{label}</small></div>;
    }
    // Array of objects → table
    if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
      const headers = Object.keys(value[0]);
      return (
        <div key={key} className="mb-4">
          <h6 className="text-primary fw-bold mb-2" style={{ fontSize: '0.8rem' }}><i className="bi bi-table me-1"></i>{label}</h6>
          <div className="table-responsive">
            <table className="table table-sm table-hover mb-0" style={{ fontSize: '0.75rem' }}>
              <thead><tr className="table-primary">{headers.map(h => <th key={h} className="text-nowrap">{h.replace(/_/g, ' ')}</th>)}</tr></thead>
              <tbody>{value.slice(0, 20).map((item, i) => (
                <tr key={i}>{headers.map(h => {
                  let cell = String(item[h] || '');
                  // Make route links clickeable
                  Object.keys(SYSTEM_ROUTES).forEach(route => {
                    if (cell.includes(route)) cell = cell.replace(route, `<a href="${route}">${SYSTEM_ROUTES[route]}</a>`);
                  });
                  return <td key={h} dangerouslySetInnerHTML={{ __html: sanitizeHTML(cell) }} />;
                })}</tr>
              ))}</tbody>
            </table>
          </div>
          {value.length > 20 && <small className="text-muted">Mostrando 20 de {value.length} registros</small>}
        </div>
      );
    }
    // Array of strings → list
    if (Array.isArray(value)) {
      const isAlert = key.includes('alerta') || key.includes('recomendaci') || key.includes('prioridad');
      return (
        <div key={key} className="mb-3">
          <h6 className="text-primary fw-bold mb-2" style={{ fontSize: '0.8rem' }}><i className={`bi ${isAlert ? 'bi-exclamation-triangle text-warning' : 'bi-list-check'} me-1`}></i>{label}</h6>
          {value.map((item, i) => (
            <div key={i} className={`${isAlert ? 'alert alert-warning py-2 px-3' : 'border-start border-2 border-primary ps-3 py-1'} mb-2`} style={{ fontSize: '0.8rem' }}
              dangerouslySetInnerHTML={{ __html: sanitizeHTML(renderMarkdown(typeof item === 'object' ? JSON.stringify(item) : String(item))) }} />
          ))}
        </div>
      );
    }
    // Object → card
    if (typeof value === 'object') {
      return (
        <div key={key} className="mb-3">
          <h6 className="text-primary fw-bold mb-2" style={{ fontSize: '0.8rem' }}>{label}</h6>
          <div className="bg-light rounded p-3">
            {Object.entries(value).map(([k, v]) => {
              if (typeof v === 'object' && v !== null) return renderValue(k, v);
              return (
                <div key={k} className="d-flex justify-content-between py-1 border-bottom" style={{ fontSize: '0.8rem' }}>
                  <span className="text-muted">{k.replace(/_/g, ' ')}</span>
                  <span className="fw-bold">{String(v)}</span>
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    return null;
  };

  const renderResultContent = (resultado) => {
    if (!resultado) return null;
    let data = resultado.resultado;
    if (typeof data === 'string') {
      try { data = JSON.parse(data); } catch {
        // Try stripping markdown fences
        let cleaned = data.trim();
        if (cleaned.startsWith('```')) {
          cleaned = cleaned.split('\n').slice(1).join('\n');
          if (cleaned.endsWith('```')) cleaned = cleaned.slice(0, -3);
          try { data = JSON.parse(cleaned.trim()); } catch {
            return <div className="small" dangerouslySetInnerHTML={{ __html: sanitizeHTML(renderMarkdown(data)) }} />;
          }
        } else {
          return <div className="small" dangerouslySetInnerHTML={{ __html: sanitizeHTML(renderMarkdown(data)) }} />;
        }
      }
    }
    if (!data || typeof data !== 'object') return <p className="text-muted">Sin datos</p>;
    if (data.texto_raw) return <div className="small" dangerouslySetInnerHTML={{ __html: sanitizeHTML(renderMarkdown(data.texto_raw)) }} />;
    return <div>{Object.entries(data).map(([k, v]) => renderValue(k, v))}</div>;
  };

  if (loading) {
    return (<div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
      <div className="text-center"><div className="spinner-border text-primary mb-3"></div><p className="text-muted">Cargando módulo de IA...</p></div>
    </div>);
  }

  return (
    <div className="container-fluid px-2 px-md-4">
      {/* Header */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-2">
        <div>
          <h4 className="mb-1 fw-bold"><i className="bi bi-robot me-2 text-primary"></i>Análisis con IA</h4>
          <small className="text-muted">Inteligencia artificial aplicada a tu negocio</small>
        </div>
        <div className="d-flex align-items-center gap-2">
          <span className={`badge ${iaServiceOnline === null ? 'bg-secondary' : iaServiceOnline ? 'bg-success' : 'bg-danger'} bg-opacity-10 ${iaServiceOnline === null ? 'text-secondary' : iaServiceOnline ? 'text-success' : 'text-danger'}`}>
            {iaServiceOnline === null
              ? <><span className="spinner-border spinner-border-sm me-1" style={{ width: 12, height: 12 }}></span>Verificando...</>
              : <><i className={`bi ${iaServiceOnline ? 'bi-check-circle' : 'bi-x-circle'} me-1`}></i>IA {iaServiceOnline ? 'Online' : 'Offline'}</>}
          </span>
          {config.anthropic_api_key?.configured && <span className="badge bg-primary bg-opacity-10 text-primary"><i className="bi bi-key me-1"></i>API Key OK</span>}
        </div>
      </div>

      {/* Tabs */}
      <ul className="nav nav-pills mb-4 gap-1 flex-wrap">
        {[{ key: 'modulos', icon: 'bi-grid-3x3-gap', label: 'Módulos' }, { key: 'chat', icon: 'bi-chat-dots', label: 'Chat IA' },
          { key: 'historial', icon: 'bi-clock-history', label: 'Historial' }, { key: 'config', icon: 'bi-gear', label: 'Configuración' }
        ].map(tab => (
          <li key={tab.key} className="nav-item"><button className={`nav-link ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)} style={{ fontSize: '0.85rem' }}><i className={`bi ${tab.icon} me-1`}></i>{tab.label}</button></li>
        ))}
      </ul>

      {/* ═══════════ TAB: MÓDULOS ═══════════ */}
      {activeTab === 'modulos' && (<>
        <div className="row g-3">
          {MODULOS.map(mod => {
            const sched = getScheduleForModule(mod.key);
            const lastResult = getLastResult(mod.key);
            const isRunning = running[mod.key];
            const phase = SPINNER_PHASES[runPhase[mod.key] || 0];
            return (
              <div key={mod.key} className="col-12 col-md-6 col-xl-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-start justify-content-between mb-3">
                      <div className="d-flex align-items-center gap-2">
                        <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 40, height: 40, background: mod.color + '15' }}>
                          <i className={`bi ${mod.icono}`} style={{ color: mod.color, fontSize: '1.2rem' }}></i>
                        </div>
                        <div>
                          <h6 className="mb-0 fw-bold" style={{ fontSize: '0.9rem' }}>{mod.nombre}</h6>
                          <small className="text-muted" style={{ fontSize: '0.75rem' }}>{mod.desc}</small>
                        </div>
                      </div>
                      {lastResult?.nivel_riesgo && (
                        <span className={`badge ${lastResult.nivel_riesgo === 'critico' ? 'bg-danger' : lastResult.nivel_riesgo === 'alto' ? 'bg-warning text-dark' : lastResult.nivel_riesgo === 'medio' ? 'bg-info' : 'bg-success'}`} style={{ fontSize: '0.65rem' }}>
                          {lastResult.nivel_riesgo.toUpperCase()}
                        </span>
                      )}
                    </div>

                    {/* Animated spinner with phases */}
                    {isRunning && (
                      <div className="mb-3 p-3 rounded text-center" style={{ background: mod.color + '10' }}>
                        <div className="spinner-border spinner-border-sm me-2" style={{ color: mod.color }}></div>
                        <small className="fw-bold" style={{ color: mod.color }}>{phase}</small>
                        <div className="progress mt-2" style={{ height: 3 }}>
                          <div className="progress-bar progress-bar-striped progress-bar-animated" style={{ width: '100%', background: mod.color }}></div>
                        </div>
                      </div>
                    )}

                    {lastResult && !isRunning && (
                      <div className="bg-light rounded p-2 mb-3" style={{ fontSize: '0.75rem' }}>
                        <div className="d-flex justify-content-between text-muted mb-1">
                          <span>Último análisis</span><span>{formatDate(lastResult.created_at)}</span>
                        </div>
                        <p className="mb-0 text-dark" style={{ maxHeight: 40, overflow: 'hidden' }}>{lastResult.resumen || 'Resultado disponible'}</p>
                      </div>
                    )}

                    <div className="d-flex align-items-center justify-content-between mb-3 p-2 bg-light rounded" style={{ fontSize: '0.8rem' }}>
                      <div className="form-check form-switch mb-0">
                        <input className="form-check-input" type="checkbox" checked={sched.habilitado || false}
                          onChange={(e) => toggleSchedule(mod.key, e.target.checked)} id={`sched-${mod.key}`} />
                        <label className="form-check-label" htmlFor={`sched-${mod.key}`}>Auto</label>
                      </div>
                      {mod.key !== 'asistente_propietario' && (
                        <select className="form-select form-select-sm" style={{ width: 'auto', fontSize: '0.7rem' }}
                          value={getFreqValue(sched)} onChange={(e) => updateFrequency(mod.key, e.target.value)} disabled={!sched.habilitado}>
                          {FRECUENCIAS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                        </select>
                      )}
                    </div>

                    <div className="d-flex gap-2">
                      <button className="btn btn-sm flex-grow-1" style={{ background: mod.color, color: '#fff', fontSize: '0.8rem' }}
                        onClick={() => mod.key === 'asistente_propietario' ? setActiveTab('chat') : ejecutarModulo(mod.key)} disabled={isRunning}>
                        {isRunning ? <><span className="spinner-border spinner-border-sm me-1"></span>Analizando...</>
                          : <><i className={`bi ${mod.key === 'asistente_propietario' ? 'bi-chat-dots' : 'bi-play-fill'} me-1`}></i>{mod.key === 'asistente_propietario' ? 'Abrir Chat' : 'Analizar'}</>}
                      </button>
                      {lastResult && <button className="btn btn-sm btn-outline-secondary" onClick={() => { setSelectedResult(lastResult); setActiveTab('historial'); }} title="Ver resultado"><i className="bi bi-eye"></i></button>}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {alertas.length > 0 && (
          <div className="card border-0 shadow-sm mt-4">
            <div className="card-header bg-danger bg-opacity-10 border-0"><h6 className="mb-0 text-danger"><i className="bi bi-exclamation-triangle me-2"></i>Alertas Recientes</h6></div>
            <div className="card-body p-0">
              {alertas.slice(0, 5).map(a => {
                const mod = MODULOS.find(m => m.key === a.modulo);
                return (<div key={a.id} className="p-3 border-bottom d-flex align-items-center gap-3" style={{ fontSize: '0.8rem' }}>
                  <i className={`bi ${mod?.icono || 'bi-robot'}`} style={{ color: mod?.color || '#666' }}></i>
                  <div className="flex-grow-1"><span className="fw-bold">{a.titulo || a.modulo}</span><small className="text-muted d-block">{formatDate(a.created_at)}</small></div>
                  {a.nivel_riesgo && <span className={`badge ${a.nivel_riesgo === 'critico' ? 'bg-danger' : a.nivel_riesgo === 'alto' ? 'bg-warning text-dark' : 'bg-info'}`}>{a.nivel_riesgo}</span>}
                </div>);
              })}
            </div>
          </div>
        )}
      </>)}

      {/* ═══════════ TAB: CHAT IA ═══════════ */}
      {activeTab === 'chat' && (
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-primary bg-opacity-10 border-0 d-flex justify-content-between align-items-center">
            <h6 className="mb-0"><i className="bi bi-chat-dots-fill me-2 text-primary"></i>Asistente IA del Propietario</h6>
            <button className="btn btn-sm btn-outline-danger" onClick={() => {
              if (chatSessionId) analisisIAAPI.clearChatHistory(chatSessionId).catch(() => {});
              setChatMessages([chatMessages[0]]); setChatSessionId(null);
            }}><i className="bi bi-trash me-1"></i>Limpiar</button>
          </div>
          <div className="card-body p-0">
            <div style={{ height: '55vh', overflowY: 'auto' }} className="p-3">
              {chatMessages.map((msg, i) => (
                <div key={i} className={`d-flex mb-3 ${msg.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}>
                  <div style={{ maxWidth: '85%' }}>
                    {msg.role === 'user' ? (
                      <div className="rounded-3 p-3 bg-primary text-white" style={{ fontSize: '0.85rem' }}>{msg.content}</div>
                    ) : (
                      <div className="rounded-3 p-3 bg-light ia-chat-response" style={{ fontSize: '0.85rem' }}
                        dangerouslySetInnerHTML={{ __html: sanitizeHTML(renderMarkdown(msg.content)) }} />
                    )}
                    {msg.tokens && <small className="text-muted" style={{ fontSize: '0.7rem' }}>{msg.tokens} tokens | {msg.time}ms</small>}
                  </div>
                </div>
              ))}
              {chatLoading && (
                <div className="d-flex justify-content-start mb-3"><div className="bg-light rounded-3 p-3">
                  <div className="d-flex gap-1">
                    <div className="spinner-grow spinner-grow-sm text-primary" style={{ width: 8, height: 8 }}></div>
                    <div className="spinner-grow spinner-grow-sm text-primary" style={{ width: 8, height: 8, animationDelay: '0.2s' }}></div>
                    <div className="spinner-grow spinner-grow-sm text-primary" style={{ width: 8, height: 8, animationDelay: '0.4s' }}></div>
                  </div>
                </div></div>
              )}
              <div ref={chatEndRef} />
            </div>
            <div className="px-3 pb-2 d-flex gap-2 flex-wrap">
              {['¿Cómo van las ventas hoy?', '¿Qué productos necesito comprar?', 'Resumen del negocio', '¿Hay créditos vencidos?'].map((s, i) => (
                <button key={i} className="btn btn-sm btn-outline-primary rounded-pill" style={{ fontSize: '0.75rem' }}
                  onClick={() => sendChat(s)} disabled={chatLoading}>{s}</button>
              ))}
            </div>
            <div className="border-top p-3">
              <div className="input-group">
                <input type="text" className="form-control" placeholder="Pregunta sobre tu negocio..."
                  value={chatInput} onChange={e => setChatInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendChat()} disabled={chatLoading} />
                <button className="btn btn-primary" onClick={sendChat} disabled={chatLoading || !chatInput.trim()}><i className="bi bi-send-fill"></i></button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ TAB: HISTORIAL ═══════════ */}
      {activeTab === 'historial' && (
        <div className="row g-3">
          <div className={selectedResult ? 'col-12 col-lg-4' : 'col-12'}>
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-0">
                <h6 className="mb-0"><i className="bi bi-clock-history me-2"></i>Historial de Análisis</h6>
                <div className="d-flex gap-2 mt-2 flex-wrap">
                  <select className="form-select form-select-sm" style={{ width: 'auto', fontSize: '0.75rem' }} value={filtroModulo} onChange={e => setFiltroModulo(e.target.value)}>
                    <option value="">Todos los módulos</option>
                    {MODULOS.map(m => <option key={m.key} value={m.key}>{m.nombre}</option>)}
                  </select>
                  <select className="form-select form-select-sm" style={{ width: 'auto', fontSize: '0.75rem' }} value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
                    <option value="">Todos</option><option value="manual">Manual</option><option value="automatico">Auto</option>
                  </select>
                </div>
              </div>
              <div className="card-body p-0" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                {filteredResultados.length === 0 ? (
                  <div className="p-4 text-center text-muted"><i className="bi bi-inbox display-4 d-block mb-2"></i>No hay análisis</div>
                ) : filteredResultados.map(r => {
                  const mod = MODULOS.find(m => m.key === r.modulo);
                  return (<div key={r.id} className={`p-3 border-bottom ${selectedResult?.id === r.id ? 'bg-primary bg-opacity-10' : ''}`}
                    style={{ cursor: 'pointer' }} onClick={() => setSelectedResult(r)}>
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <i className={`bi ${mod?.icono || 'bi-robot'}`} style={{ color: mod?.color || '#666' }}></i>
                      <span className="fw-bold" style={{ fontSize: '0.85rem' }}>{mod?.nombre || r.modulo}</span>
                      {r.nivel_riesgo && <span className={`badge ${r.nivel_riesgo === 'critico' ? 'bg-danger' : r.nivel_riesgo === 'alto' ? 'bg-warning text-dark' : 'bg-info'}`} style={{ fontSize: '0.6rem' }}>{r.nivel_riesgo}</span>}
                      <span className={`badge ms-auto ${r.estado === 'completado' ? 'bg-success' : 'bg-danger'} bg-opacity-10 ${r.estado === 'completado' ? 'text-success' : 'text-danger'}`} style={{ fontSize: '0.65rem' }}>
                        {r.tipo === 'automatico' ? 'Auto ' : ''}{r.estado}
                      </span>
                    </div>
                    <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>{formatDate(r.created_at)} | {r.tokens_usados || 0} tokens | {r.tiempo_ejecucion_ms || 0}ms</small>
                    {r.resumen && <small className="text-dark d-block mt-1" style={{ fontSize: '0.7rem', maxHeight: 32, overflow: 'hidden' }}>{r.resumen}</small>}
                  </div>);
                })}
              </div>
            </div>
          </div>

          {selectedResult && (
            <div className="col-12 col-lg-8">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-0 d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="mb-0">{selectedResult.titulo || selectedResult.modulo}</h6>
                    <small className="text-muted">{formatDate(selectedResult.created_at)} | {selectedResult.modelo_usado}</small>
                  </div>
                  <div className="d-flex gap-1">
                    <button className="btn btn-sm btn-outline-danger" title="PDF" onClick={() => exportar(selectedResult.id, 'pdf')}><i className="bi bi-file-pdf"></i></button>
                    <button className="btn btn-sm btn-outline-success" title="Excel" onClick={() => exportar(selectedResult.id, 'excel')}><i className="bi bi-file-earmark-excel"></i></button>
                    <button className="btn btn-sm btn-outline-primary" title="Email" onClick={() => enviarEmailResult(selectedResult.id)}><i className="bi bi-envelope"></i></button>
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => setSelectedResult(null)}><i className="bi bi-x-lg"></i></button>
                  </div>
                </div>
                <div className="card-body" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                  {selectedResult.error ? (
                    <div className="alert alert-danger"><i className="bi bi-exclamation-triangle me-2"></i>{selectedResult.error}</div>
                  ) : (<>
                    {selectedResult.nivel_riesgo && (
                      <div className={`alert ${selectedResult.nivel_riesgo === 'critico' ? 'alert-danger' : selectedResult.nivel_riesgo === 'alto' ? 'alert-warning' : 'alert-info'} py-2`}>
                        <i className="bi bi-shield-exclamation me-2"></i><strong>Nivel de riesgo:</strong> {selectedResult.nivel_riesgo.toUpperCase()}
                      </div>
                    )}
                    {selectedResult.resumen && (
                      <div className="alert bg-primary bg-opacity-10 border-0 mb-3">
                        <i className="bi bi-lightbulb me-2 text-primary"></i><strong>Resumen:</strong>
                        <div className="mt-1 small" dangerouslySetInnerHTML={{ __html: sanitizeHTML(renderMarkdown(selectedResult.resumen)) }} />
                      </div>
                    )}
                    {renderResultContent(selectedResult)}
                  </>)}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══════════ TAB: CONFIGURACIÓN ═══════════ */}
      {activeTab === 'config' && (
        <div className="row g-3">
          <div className="col-12 col-lg-6">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-0"><h6 className="mb-0"><i className="bi bi-key me-2 text-warning"></i>Configuración IA</h6></div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label small fw-semibold">API Key de Anthropic</label>
                  <input type="password" className="form-control" placeholder={config.anthropic_api_key?.configured ? 'Configurada (ingrese nueva para cambiar)' : 'sk-ant-...'}
                    value={configForm.apiKey} onChange={e => setConfigForm(f => ({ ...f, apiKey: e.target.value }))} />
                  <div className="form-text d-flex align-items-center justify-content-between">
                    {config.anthropic_api_key?.configured
                      ? <span className="text-success"><i className="bi bi-check-circle me-1"></i>Encriptada en BD</span>
                      : <span className="text-warning"><i className="bi bi-exclamation-triangle me-1"></i>Requerida</span>}
                    {config.anthropic_api_key?.configured && (
                      <button className="btn btn-sm btn-outline-danger py-0 px-2" style={{ fontSize: '0.7rem' }}
                        onClick={async () => { if (window.confirm('¿Eliminar la API Key?')) { await analisisIAAPI.deleteConfig('anthropic_api_key'); toast.success('API Key eliminada'); loadData(); } }}>
                        <i className="bi bi-trash me-1"></i>Eliminar
                      </button>
                    )}
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label small fw-semibold">Modelo Claude</label>
                  <select className="form-select" value={configForm.modelo} onChange={e => setConfigForm(f => ({ ...f, modelo: e.target.value }))}>
                    <optgroup label="Claude 4.6"><option value="claude-opus-4-6">Opus 4.6 (Máxima capacidad)</option><option value="claude-sonnet-4-6">Sonnet 4.6 (Recomendado)</option></optgroup>
                    <optgroup label="Claude 4.5"><option value="claude-opus-4-5-20251101">Opus 4.5</option><option value="claude-sonnet-4-5-20250929">Sonnet 4.5</option><option value="claude-haiku-4-5-20251001">Haiku 4.5 (Económico)</option></optgroup>
                    <optgroup label="Claude 4"><option value="claude-opus-4-20250514">Opus 4</option><option value="claude-sonnet-4-20250514">Sonnet 4</option></optgroup>
                    <optgroup label="Legacy"><option value="claude-3-7-sonnet-20250219">3.7 Sonnet</option><option value="claude-3-5-sonnet-20241022">3.5 Sonnet v2</option><option value="claude-3-5-haiku-20241022">3.5 Haiku</option></optgroup>
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label small fw-semibold">Email para reportes automáticos</label>
                  <input type="email" className="form-control" placeholder="admin@oftalmetryc.com"
                    value={configForm.emailDestinatario} onChange={e => setConfigForm(f => ({ ...f, emailDestinatario: e.target.value }))} />
                  <div className="form-text">Los análisis automáticos se enviarán a este email</div>
                </div>
                {/* SMTP Configuration */}
                <div className="border-top pt-3 mt-3">
                  <h6 className="fw-bold small mb-3"><i className="bi bi-envelope-at me-1 text-info"></i>Configuración SMTP (para envío de emails)
                    {smtpConfigured && <span className="badge bg-success bg-opacity-10 text-success ms-2" style={{ fontSize: '0.65rem' }}><i className="bi bi-check-circle me-1"></i>Configurado</span>}
                  </h6>
                  <div className="row g-2 mb-2">
                    <div className="col-8">
                      <input type="text" className="form-control form-control-sm" placeholder="Host SMTP (ej: smtp.gmail.com)"
                        value={configForm.smtpHost} onChange={e => setConfigForm(f => ({ ...f, smtpHost: e.target.value }))} />
                    </div>
                    <div className="col-4">
                      <select className="form-select form-select-sm" value={configForm.smtpPort} onChange={e => setConfigForm(f => ({ ...f, smtpPort: e.target.value }))}>
                        <option value="587">587 (TLS)</option><option value="465">465 (SSL)</option><option value="25">25</option>
                      </select>
                    </div>
                  </div>
                  <div className="mb-2">
                    <input type="text" className="form-control form-control-sm" placeholder="Usuario SMTP (ej: tu@gmail.com)"
                      value={configForm.smtpUser} onChange={e => setConfigForm(f => ({ ...f, smtpUser: e.target.value }))} />
                  </div>
                  <div className="mb-2">
                    <input type="password" className="form-control form-control-sm" placeholder={smtpConfigured ? 'Contraseña SMTP (dejar vacío para mantener)' : 'Contraseña SMTP o App Password'}
                      value={configForm.smtpPassword} onChange={e => setConfigForm(f => ({ ...f, smtpPassword: e.target.value }))} />
                  </div>
                  <div className="mb-2">
                    <input type="email" className="form-control form-control-sm" placeholder="Email remitente (From) - opcional"
                      value={configForm.smtpFrom} onChange={e => setConfigForm(f => ({ ...f, smtpFrom: e.target.value }))} />
                    <div className="form-text" style={{ fontSize: '0.7rem' }}>Si usas Gmail, necesitas una App Password (no la contraseña normal)</div>
                  </div>
                </div>
                <button className="btn btn-primary mt-2" onClick={saveConfig}><i className="bi bi-check-lg me-1"></i>Guardar Configuración</button>
              </div>
            </div>
          </div>
          <div className="col-12 col-lg-6">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-0"><h6 className="mb-0"><i className="bi bi-info-circle me-2 text-info"></i>Estado del Servicio</h6></div>
              <div className="card-body">
                <div className="d-flex align-items-center gap-3 mb-3 p-3 rounded" style={{ background: iaServiceOnline === null ? '#f8f9fa' : iaServiceOnline ? '#f0fdf4' : '#fef2f2' }}>
                  {iaServiceOnline === null
                    ? <span className="spinner-border text-secondary fs-3" style={{ width: 28, height: 28 }}></span>
                    : <i className={`bi ${iaServiceOnline ? 'bi-check-circle-fill text-success' : 'bi-x-circle-fill text-danger'} fs-3`}></i>}
                  <div><div className="fw-bold">{iaServiceOnline === null ? 'Verificando servicio...' : iaServiceOnline ? 'Servicio IA Online' : 'Servicio IA Offline'}</div><small className="text-muted">{iaServiceOnline === null ? 'Comprobando conexión con microservicio Python' : iaServiceOnline ? 'Microservicio Python operativo' : 'No se puede conectar'}</small></div>
                </div>
                <h6 className="fw-bold small mt-4 mb-2">Programación Automática</h6>
                {schedule.map(s => {
                  const mod = MODULOS.find(m => m.key === s.modulo);
                  return (<div key={s.id} className="d-flex align-items-center justify-content-between py-2 border-bottom" style={{ fontSize: '0.8rem' }}>
                    <div className="d-flex align-items-center gap-2"><i className={`bi ${mod?.icono || 'bi-robot'}`} style={{ color: mod?.color || '#666' }}></i><span>{s.nombre_modulo}</span></div>
                    <span className={`badge ${s.habilitado ? 'bg-success' : 'bg-secondary'} bg-opacity-10 ${s.habilitado ? 'text-success' : 'text-secondary'}`}>{s.habilitado ? getFreqLabel(s) : 'Off'}</span>
                  </div>);
                })}
              </div>
            </div>
          </div>
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-0"><h6 className="mb-0"><i className="bi bi-bar-chart me-2 text-primary"></i>Estadísticas de Uso</h6></div>
              <div className="card-body"><div className="row g-3">
                {[{ label: 'Análisis', value: resultados.length, color: 'primary' }, { label: 'Tokens', value: resultados.reduce((s, r) => s + (r.tokens_usados || 0), 0).toLocaleString(), color: 'success' },
                  { label: 'Exitosos', value: resultados.filter(r => r.estado === 'completado').length, color: 'info' }, { label: 'Errores', value: resultados.filter(r => r.estado === 'error').length, color: 'danger' }
                ].map((stat, i) => (<div key={i} className="col-6 col-md-3"><div className="text-center p-3 bg-light rounded">
                  <div className={`fs-4 fw-bold text-${stat.color}`}>{stat.value}</div><small className="text-muted">{stat.label}</small>
                </div></div>))}
              </div></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalisisIA;
